<template>
	<BaseSnackbar :display="saveStatus !== 'hide'">
		<div
			class="rounded-2xl border-2 border-gray-300 bg-white p-6 dark:border-gray-600 dark:bg-gray-800"
		>
			<div v-if="saveStatus === 'saving'">
				<div class="flex w-fit items-center gap-4">
					<LoadingSpinner class="h-14 w-14" />
					<span class="text-xl font-semibold text-gray-800 dark:text-gray-200">
						Saving your changes, one moment...
					</span>
				</div>
				<BaseExpand :expanded="bullets.length > 0">
					<transition-group
						name="fade-expand"
						tag="ul"
						class="mt-4 list-inside list-disc border-t border-gray-300 pt-2 text-gray-700 dark:border-gray-600 dark:text-gray-300"
					>
						<li v-for="b in bullets" :key="b">
							{{ b }}
						</li>
					</transition-group>
				</BaseExpand>
			</div>

			<div v-if="saveStatus === 'saved'" class="grid gap-4">
				<div class="flex items-center gap-4">
					<FAIcon
						:icon="rndIcon('party')"
						class="h-8 w-8 rounded-full border-4 border-blue-300 bg-blue-50 p-2 text-blue-600"
					/>
					<span class="text-xl font-semibold text-gray-800 dark:text-gray-200">
						Nice Work -- Your domain is updated!
					</span>
				</div>
				<div class="text-gray-700 dark:text-gray-300">
					<div v-if="saveDomain">
						Your changes are in production, and can be viewed at
						<a
							class="truncate leading-none"
							target="_blank"
							:href="`https://${saveDomain}${saveRoute}`"
						>
							<FAIcon icon="fa-solid fa-arrow-up-right-from-square" />
							https://{{ saveDomain }}{{ saveRoute }}
						</a>
					</div>
					<div v-else>
						Click the <b>Live View</b> button or use a web browser or a mobile device to
						see the changes.
					</div>
				</div>
				<HelpPleaseNote>
					Changes that are pending approval will not appear in the live view
					<span v-if="saveDomain">or on {{ saveDomain }}</span> until they have been
					approved.
				</HelpPleaseNote>
			</div>

			<div v-if="saveStatus === 'error'" class="grid gap-4">
				<div class="flex items-center gap-4">
					<FAIcon
						icon="skull"
						class="h-8 w-8 rounded-full border-4 border-red-300 bg-red-50 p-2 text-red-600"
					/>
					<span class="text-xl font-semibold text-gray-800 dark:text-gray-200">
						Those changes were not saved!
					</span>
				</div>
				<div
					class="grid gap-2 rounded-xl border-2 border-x-red-300 border-y-transparent p-4 font-mono text-red-800 dark:text-red-400"
				>
					<div class="font-bold">Error Details:</div>
					<div class="text-sm">{{ saveError?.message }}</div>
				</div>
				<div class="text-gray-700 dark:text-gray-300">
					Try waiting a few minutes and saving your changes again. If this error persists,
					please make a note of the
					<span class="text-red-600 dark:text-red-400">Error Details</span> and contact us
					for support.
				</div>
			</div>
		</div>
	</BaseSnackbar>
</template>

<script setup>
import { ref, watch } from 'vue';
import { rndIcon } from '@/utils';
import { storeToRefs } from 'pinia';

import BaseSnackbar from '@/components/ui/BaseSnackbar';
import BaseExpand from '@/components/ui/BaseExpand';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import HelpPleaseNote from '@/components/MXEditor/help/HelpPleaseNote.vue';
import useEditorChangesStore from '@/stores/editorChanges';

const bullets = ref([]);
const editorChangesStore = useEditorChangesStore();
const { saveStatus, saveDomain, saveRoute, saveError } = storeToRefs(editorChangesStore);

const bulletList = [
	'Storing your updates',
	'Compiling the new configuration',
	'Rendering your fresh, updated domain',
];

const bulletTimeouts = ref([]);

watch(
	() => saveStatus,
	status => {
		bullets.value = [];
		bulletTimeouts.value.forEach(timeout => clearTimeout(timeout));
		bulletTimeouts.value = [];

		if (status.value === 'saving') {
			for (let i = 0; i < bulletList.length; i++) {
				const b = bulletList[i];
				const timeout = setTimeout(
					() => {
						bullets.value.push(b);
					},
					i * 1500 + 2000
				);
				bulletTimeouts.value.push(timeout);
			}
		}
	},
	{ immediate: true, deep: true }
);
</script>

<style scoped>
.fade-expand-enter-active,
.fade-expand-leave-active {
	transition: all 0.5s;
}
.fade-expand-enter-from,
.fade-expand-leave-to {
	max-height: 0px;
	opacity: 0;
}
.fade-expand-enter-to,
.fade-expand-leave-from {
	max-height: 32px;
	opacity: 100;
}
</style>
